import { __decorate, __metadata } from "tslib";
import { Serialize } from '@ftr/serialization';
import { LocalDate } from '@js-joda/core';
/**
 * Represents a LocalDate time range. Contains two LocalDate instances, one that
 * specifies the start of the range and another that specifies the end of the range.
 */
export class LocalDateRange {
    /**
     * @param {LocalDate} start of the time range.
     * @param {LocalDate} end of the time range.
     */
    constructor(start, end) {
        this.start = start;
        this.end = end;
    }
    static parse(start, end) {
        return new LocalDateRange(LocalDate.parse(start), LocalDate.parse(end));
    }
    /**
     * Returns the range with the earliest start date and time from the given array.
     * @throws Error if array is empty
     */
    static getEarliest(ranges) {
        if (!ranges.length) {
            throw new Error('Empty array');
        }
        return [...ranges].sort((a, b) => a.start.compareTo(b.start)).shift();
    }
    /**
     * Returns the range with the latest end date and time from the given array.
     * @throws Error if array is empty
     */
    static getLatest(ranges) {
        if (!ranges.length) {
            throw new Error('Empty array');
        }
        return [...ranges].sort((a, b) => a.end.compareTo(b.end)).pop();
    }
    /**
     * Returns `true` if this range contains the given date-time.
     *
     * This is inclusive of start only.
     */
    contains(localDate) {
        return this.start.compareTo(localDate) <= 0 && localDate.compareTo(this.end) < 0;
    }
    /**
     * Returns `true` if this range encloses the given range.
     *
     * This is inclusive of start and end.
     */
    encloses(other) {
        return this.start.compareTo(other.start) <= 0 && other.end.compareTo(this.end) <= 0;
    }
    /**
     * Returns `true` if this range is equal to the given range.
     */
    equals(other) {
        return this.start.equals(other.start) && this.end.equals(other.end);
    }
    /**
     * Returns `true` if this range overlaps the given range.
     */
    overlaps(other) {
        return other.equals(this) || (this.start.compareTo(other.end) < 0 && other.start.compareTo(this.end) < 0);
    }
    toString() {
        return `${this.start.toString()} -> ${this.end.toString()}`;
    }
}
__decorate([
    Serialize(),
    __metadata("design:type", LocalDate)
], LocalDateRange.prototype, "start", void 0);
__decorate([
    Serialize(),
    __metadata("design:type", LocalDate
    /**
     * @param {LocalDate} start of the time range.
     * @param {LocalDate} end of the time range.
     */
    )
], LocalDateRange.prototype, "end", void 0);
